"use client";

import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plus, RefreshCw, Send } from "lucide-react";
import { EmptyState, InlineNotice, Section, StatTile, primaryBtn, secondaryBtn } from "../ui";
import { formatCount, timeAgo } from "./format";
import { DELIVERY_STATUS_UI, describeAlert } from "./vocab";
import { useWebhookSummary } from "./useWebhookSummary";

// ────────────────────────────────────────────────────────────────────────────
// WebhookSummaryCard — the "Webhooks" panel on API Settings. A live,
// read-only digest (counts, last delivery, the most urgent alert) with a
// link into /developers/webhooks, where everything is actually managed.
// It reads the shared summary poller, so it costs no extra request.
// ────────────────────────────────────────────────────────────────────────────

// Mirrors the backend's durable quota (5 endpoints per merchant); the
// summary payload does not carry it.
const ENDPOINT_LIMIT = 5;

/**
 * @param wallet Redux wallet (null keeps the shared poller idle)
 * @param email  Redux email
 */
export function WebhookSummaryCard({
  wallet,
  email,
}: {
  wallet: string | null;
  email: string | null;
}) {
  const { summary, loading, refresh } = useWebhookSummary(wallet, email);

  // This card only renders for an APPROVED merchant. A cached
  // `merchant: false` means the summary predates the approval (the poller
  // is in its 10-minute slow mode) — ask again, once.
  const recheckedRef = useRef(false);
  useEffect(() => {
    if (summary && !summary.merchant && !recheckedRef.current) {
      recheckedRef.current = true;
      void refresh();
    }
  }, [summary, refresh]);

  let body: React.ReactNode;
  if (!summary) {
    body = loading ? (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 animate-pulse" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-xl border border-gray-800 bg-gray-900/40 p-3">
            <div className="h-2.5 w-16 rounded bg-gray-800/70" />
            <div className="h-3.5 w-10 rounded bg-gray-800/50 mt-2.5" />
          </div>
        ))}
      </div>
    ) : (
      <InlineNotice
        title="Couldn't load webhook status"
        action={
          <button
            type="button"
            onClick={() => void refresh()}
            className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-white"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Retry
          </button>
        }
      >
        Your endpoints keep receiving events. This is only the status display.
      </InlineNotice>
    );
  } else if (!summary.merchant || summary.endpointCount === 0) {
    body = (
      <EmptyState
        icon={Send}
        title="No webhook endpoints yet."
        hint="Add an endpoint to be notified the moment a buyer pays."
        action={
          <Link to="/developers/webhooks?new=1" className={primaryBtn}>
            <Plus className="w-4 h-4" /> Add endpoint
          </Link>
        }
      />
    );
  } else {
    const healthy = Math.max(0, summary.enabledCount - summary.failingCount);
    const last = summary.lastDelivery;
    const alert = summary.alerts[0] || null;
    const copy = alert ? describeAlert(alert) : null;
    const critical = alert?.severity === "critical";
    body = (
      <>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatTile
            label="Endpoints"
            value={`${formatCount(summary.endpointCount)} / ${ENDPOINT_LIMIT}`}
          />
          <StatTile label="Healthy" value={formatCount(healthy)} tone={healthy > 0 ? "green" : "default"} />
          <StatTile
            label="Failing"
            value={formatCount(summary.failingCount)}
            tone={summary.failingCount > 0 ? "amber" : "default"}
          />
          <StatTile
            label="Last delivery"
            value={
              last
                ? `${DELIVERY_STATUS_UI[last.status]?.label ?? last.status} · ${timeAgo(last.at)}`
                : "None yet"
            }
          />
        </div>
        {alert && copy && (
          <InlineNotice
            tone={critical ? "error" : "warn"}
            role={critical ? "alert" : "status"}
            title={copy.title}
            className="mt-3"
            action={
              <Link
                to={copy.ctaTo}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold ${
                  critical ? "text-red-200 hover:text-white" : "text-amber-300 hover:text-white"
                }`}
              >
                {copy.ctaLabel} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            }
          >
            {copy.body}
            {summary.alerts.length > 1 &&
              ` ${summary.alerts.length - 1} more ${
                summary.alerts.length - 1 === 1 ? "endpoint needs" : "endpoints need"
              } attention.`}
          </InlineNotice>
        )}
      </>
    );
  }

  return (
    <Section
      icon={<Send className="w-4 h-4 text-[#61CE70]" />}
      title="Webhooks"
      desc="Signed event delivery to your server, with retries, alerts and a delivery log."
      action={
        <Link to="/developers/webhooks" className={`${secondaryBtn} shrink-0`}>
          Manage webhooks
        </Link>
      }
    >
      {body}
    </Section>
  );
}
